/**
 * The one `<input type=file>` the page picks receipts with — shared by every template and every
 * screen, so no screen renders an input of its own and no two screens can drift apart.
 *
 * Why it looks the way it does:
 *
 * - **One input for the life of the page, created on the first press and attached to `body`.** It
 *   lives outside the React tree, so a re-render or a screen change never removes it while the OS
 *   dialog is up. It is attached rather than kept detached in memory: Safari may collect a detached
 *   input while its dialog is open, and then `change` never arrives.
 * - **Hidden by opacity and position, not `display: none`.** Older Safari does not open the dialog
 *   of an input that is out of the layout.
 * - **The file goes to the screen that is on display now**, not to the one that was pressed. The
 *   page polls while the client browses his storage, and the screen can change under the dialog;
 *   the file is handed to whatever picker screen is mounted when it arrives.
 * - **No `accept` on Android.** Several Android pickers honour a PDF filter by hiding Drive,
 *   Downloads and third-party file managers, so the client cannot find his receipt at all. The
 *   format is checked after the pick instead (`prepareReceipt`). Elsewhere `accept` stays: without it
 *   iOS leads with the photo library and camera, and clients of PDF-only merchants would attach
 *   screenshots only to have them refused.
 *
 * The pick-step telemetry (`services/receiptPick`) is watched here, next to the input it describes.
 */
import { PickWatch, createPickWatch } from './receiptPick';
import { getReceiptInputAccept } from './receiptUploadPolicy';
import { getAnalyticsContext } from '../analytics/currentContext';

export interface ReceiptFileReceiver {
  /** A file came out of the dialog. It is not validated yet. */
  receive: (file: File) => void;
  /** Fruitless presses so far in this visit; 0 means a file was taken and the count reset. */
  fruitless: (count: number) => void;
}

const HIDDEN_STYLE: Partial<CSSStyleDeclaration> = {
  position: 'fixed',
  top: '0',
  left: '-10000px',
  width: '1px',
  height: '1px',
  opacity: '0',
  pointerEvents: 'none',
};

export const isAndroid = (): boolean => {
  try {
    return /android/i.test(navigator.userAgent);
  } catch {
    return false;
  }
};

/** The `accept` handed to the OS dialog: none on Android, the merchant's formats elsewhere. */
export const receiptPickerAccept = (merchant: string | undefined, android = isAndroid(), concierge = false): string =>
  android ? '' : getReceiptInputAccept(merchant, concierge);

// Mounted picker screens, the most recent last. The last one is "the screen on display".
let receivers: ReceiptFileReceiver[] = [];
let input: HTMLInputElement | null = null;
let fruitless = 0;
let watch: PickWatch | null = null;

const current = (): ReceiptFileReceiver | undefined => receivers[receivers.length - 1];

const pickWatch = (): PickWatch => {
  if (!watch) {
    watch = createPickWatch({
      getContext: getAnalyticsContext,
      onFruitless: (count) => {
        fruitless = count;
        receivers.forEach((receiver) => receiver.fruitless(count));
      },
    });
  }
  return watch;
};

const onChange = () => {
  const picked = input?.files?.[0];
  if (!picked) {
    // A real outcome, not a no-op: the client opened the dialog and came back with nothing.
    pickWatch().empty();
    return;
  }
  pickWatch().picked();
  current()?.receive(picked);
};

// `cancel` needs a native listener: React has no `onCancel` for a file input, and this input is not
// React's anyway.
const onCancel = () => pickWatch().cancelled();

const ensureInput = (): HTMLInputElement => {
  if (input && input.isConnected) return input;
  input = document.createElement('input');
  input.type = 'file';
  input.tabIndex = -1;
  input.setAttribute('aria-hidden', 'true');
  input.setAttribute('data-receipt-file-input', '');
  Object.assign(input.style, HIDDEN_STYLE);
  input.addEventListener('change', onChange);
  input.addEventListener('cancel', onCancel);
  document.body.appendChild(input);
  return input;
};

/** A picker screen mounted. Returns the unmount. */
export const registerReceiptReceiver = (receiver: ReceiptFileReceiver): (() => void) => {
  receivers = [...receivers.filter((r) => r !== receiver), receiver];
  receiver.fruitless(fruitless);
  return () => {
    receivers = receivers.filter((r) => r !== receiver);
    // No picker screen left to take a file: stop the watchdog, as the screen's own unmount used to.
    if (receivers.length === 0) watch?.dispose();
  };
};

/** The press on «прикрепить чек». Must run inside the click handler to keep the user activation. */
export const openReceiptPicker = (receiver: ReceiptFileReceiver, merchant: string | undefined, concierge = false): void => {
  // The pressing screen is the one on display.
  receivers = [...receivers.filter((r) => r !== receiver), receiver];
  const el = ensureInput();
  const accept = receiptPickerAccept(merchant, isAndroid(), concierge);
  if (accept) el.setAttribute('accept', accept);
  else el.removeAttribute('accept');
  // Cleared before every open, never right after a pick: re-picking the identical file fires no
  // `change` otherwise, while clearing it straight away could release a lazily provided file (a cloud
  // pick on Android) before the screen has read it.
  el.value = '';
  pickWatch().opened();
  try {
    el.click();
  } catch {
    // A browser that refuses the programmatic click leaves the watchdog to report the silence.
  }
};

export const _resetReceiptFileInputForTests = (): void => {
  watch?.dispose();
  watch = null;
  input?.remove();
  input = null;
  receivers = [];
  fruitless = 0;
};
